import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { careTeamService } from '../../../services/careTeamService';
import api from '../../../config/api';

const BookAppointment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  // Get initial values from URL parameters
  const initialProviderId = searchParams.get('provider');
  const initialDate = searchParams.get('date');
  const initialTime = searchParams.get('time');
  const initialService = searchParams.get('service');

  // Form State
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProvider, setSelectedProvider] = useState(initialProviderId || '');
  const [appointmentType, setAppointmentType] = useState(initialService || 'consultation');
  const [consultationMode, setConsultationMode] = useState('video');
  const [selectedDate, setSelectedDate] = useState(initialDate || '');
  const [selectedTime, setSelectedTime] = useState(initialTime || '');
  const [duration, setDuration] = useState('60');
  
  // Patient Information
  const [isNewPatient, setIsNewPatient] = useState(true);
  const [patientInfo, setPatientInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    city: '',
    state: 'Kerala',
    pincode: '',
    emergencyContact: '',
    emergencyPhone: '',
    customerId: '' // For existing patients
  });

  // Medical Information
  const [medicalInfo, setMedicalInfo] = useState({
    primaryConcern: '',
    symptoms: [],
    previousTherapy: 'no',
    medications: '',
    allergies: '',
    additionalNotes: ''
  });

  // Payment Information
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [insuranceInfo, setInsuranceInfo] = useState({
    hasInsurance: false,
    provider: '',
    policyNumber: '',
    groupNumber: ''
  });

  // Form validation errors
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProviderData, setSelectedProviderData] = useState(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'success' or 'error'
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  
  // Terms agreement
  const [agreedToTerms, setAgreedToTerms] = useState(false);


  // Appointment types
  const appointmentTypes = [
    { value: 'consultation', label: 'Standard Consultation', icon: '🏥', duration: '60-75 min' },
    // { value: 'therapy', label: 'Therapy Session', icon: '💬', duration: '45-60 min' },
    // { value: 'follow-up', label: 'Follow-up Visit', icon: '📋', duration: '30-45 min' },
    // { value: 'assessment', label: 'Psychological Assessment', icon: '📊', duration: '90-120 min' },
    // { value: 'medication', label: 'Medication Review', icon: '💊', duration: '20-30 min' },
    // { value: 'group', label: 'Group Therapy', icon: '👥', duration: '60-90 min' }
  ];

  // Common mental health symptoms
  const commonSymptoms = [
    'Anxiety', 'Depression', 'Stress', 'Sleep Issues',
    'Mood Swings', 'Panic Attacks', 'Low Self-esteem',
    'Relationship Issues', 'Grief', 'Trauma', 'Anger Issues',
    'Eating Concerns', 'Substance Use', 'Work Stress', 'Other'
  ];

  // Load providers on mount
  useEffect(() => {
    loadProviders();
  }, []);

  // Load providers from API
  const loadProviders = async () => {
    try {
      setLoadingProviders(true);
      setError(null);
      
      const response = await careTeamService.getPublicCareTeamList({
        page: 1,
        per_page: 100
      });

      if (response.success) {
        setProviders(response.data.items);
        
        // If provider is pre-selected from URL, set it as selected
        if (initialProviderId) {
          const preSelectedProvider = response.data.items.find(p => p.user_id === initialProviderId);
          if (preSelectedProvider) {
            setSelectedProvider(preSelectedProvider.user_id);
            setSelectedProviderData(preSelectedProvider);
          }
        }
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError({
        message: 'Failed to load providers. Please try again.',
        status: 500
      });
      console.error('Error loading providers:', err);
    } finally {
      setLoadingProviders(false);
    }
  };

  // Load free slots for selected provider and date
  const loadFreeSlots = async (userId, date) => {
    try {
      setLoadingSlots(true);
      setError(null);
      
      const dateObj = new Date(date);
      // Convert to Monday=0, Sunday=6 format
      const weekDay = (dateObj.getDay() + 6) % 7; // 0=Monday, 1=Tuesday, ..., 6=Sunday
      
      const response = await api.get(`/planify/event/public/user/${userId}/free-slot`, {
        params: {
          date: date,
          week_day: weekDay
        }
      });

      if (response.data && Array.isArray(response.data)) {
        // Format the slots to show start_time and end_time
        const formattedSlots = response.data.map(slot => {
          const startTime = new Date(slot.start_time);
          const endTime = new Date(slot.end_time);
          
          // Format time to 12-hour format
          const formatTime = (date) => {
            return date.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          };
          
          return {
            id: slot.availability_id,
            start_time: slot.start_time,
            end_time: slot.end_time,
            display_time: `${formatTime(startTime)} - ${formatTime(endTime)}`,
            start_display: formatTime(startTime),
            end_display: formatTime(endTime)
          };
        });
        
        setAvailableSlots(formattedSlots);
      } else {
        setAvailableSlots([]);
      }
    } catch (err) {
      console.error('Error loading free slots:', err);
      setAvailableSlots([]);
      // Don't set error for slots as it's not critical
    } finally {
      setLoadingSlots(false);
    }
  };

  // Generate next 30 days
  const getNext30Days = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        value: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        }),
        dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'long' })
      });
    }
    return dates;
  };

  // Form validation for each step
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!selectedProvider) newErrors.provider = 'Please select a provider';
        if (!appointmentType) newErrors.type = 'Please select appointment type';
        if (!consultationMode) newErrors.mode = 'Please select consultation mode';
        break;
      
      case 2:
        if (!selectedDate) newErrors.date = 'Please select a date';
        if (!selectedTime) newErrors.time = 'Please select a time';
        break;
      
      case 3:
        if (!patientInfo.firstName) newErrors.firstName = 'First name is required';
        if (!patientInfo.lastName) newErrors.lastName = 'Last name is required';
        // if (!patientInfo.email) newErrors.email = 'Email is required';
        if (patientInfo.email && !/\S+@\S+\.\S+/.test(patientInfo.email)) {
          newErrors.email = 'Invalid email format';
        }
        if (!patientInfo.phone) newErrors.phone = 'Phone number is required';
        else if (!/^\+?[1-9]\d{9,14}$/.test(patientInfo.phone.replace(/\s/g, ''))) {
          newErrors.phone = 'Invalid phone number';
        }
        
        // Only validate these fields for new patients
        if (isNewPatient) {
        if (!patientInfo.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        if (!patientInfo.gender) newErrors.gender = 'Please select gender';
        }
        
        // Validate customer ID for existing patients
        if (!isNewPatient && !patientInfo.customerId) {
          newErrors.customerId = 'Customer ID is required for existing patients';
        }
        break;
      
      case 4:
        if (!medicalInfo.primaryConcern) {
          newErrors.primaryConcern = 'Please describe your primary concern';
        }
        if (medicalInfo.symptoms.length === 0) {
          newErrors.symptoms = 'Please select at least one symptom';
        }
        break;
      
      case 5:
        // Validate terms agreement
        if (!agreedToTerms) {
          newErrors.terms = 'You must agree to the Terms & Conditions and Privacy Policy';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle provider selection
  const handleProviderSelection = (providerId) => {
    setSelectedProvider(providerId);
    const provider = providers.find(p => p.user_id === providerId);
    setSelectedProviderData(provider);
  };

  // Handle date selection
  const handleDateSelection = (date) => {
    setSelectedDate(date);
    setSelectedTime(''); // Reset time when date changes
    
    // Load free slots for selected provider and date
    if (selectedProvider) {
      loadFreeSlots(selectedProvider, date);
    }
  };

  // Handle step navigation
  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    
    try {
      // Get selected slot data
      const selectedSlot = availableSlots.find(slot => slot.display_time === selectedTime);
      if (!selectedSlot) {
        throw new Error('Selected time slot not found');
      }

      // Get selected provider data
      const provider = selectedProviderData;
      if (!provider) {
        throw new Error('Provider not selected');
      }

      // Convert ISO datetime to API time format (HH:MM:SS.sssZ)
      const formatTimeForAPI = (isoDateTime) => {
        const date = new Date(isoDateTime);
        const timeString = date.toTimeString().split(' ')[0]; // HH:MM:SS
        return `${timeString}.000Z`; // Convert to HH:MM:SS.sssZ format
      };

      // Prepare booking payload
      const bookingPayload = {
        booking_data: {
          availability_id: selectedSlot.id,
          booked_by_email: patientInfo?.email || "",
          booked_by_name: `${patientInfo.firstName} ${patientInfo.lastName}`,
          custom_fields: {
            emergency_contact: patientInfo.emergencyPhone,
            preferred_language: "English",
            special_requirements: medicalInfo.additionalNotes || "",
            symptoms: medicalInfo.symptoms.join(", "),
            address: patientInfo.address,
            city: patientInfo.city,
            state: patientInfo.state,
            pincode: patientInfo.pincode,
            date_of_birth: patientInfo.dateOfBirth,
            gender: patientInfo.gender,
            status: "waiting",
          },
          customer_id: !isNewPatient ? patientInfo.customerId : undefined,
          end_date: selectedDate,
          end_time: formatTimeForAPI(selectedSlot.end_time),
          event_type_id: provider.event_type_id, // Using event_type_id from provider details
          mobile: patientInfo.phone,
          note: medicalInfo.primaryConcern,
          start_date: selectedDate,
          start_time: formatTimeForAPI(selectedSlot.start_time),
          timezone: "Asia/Kolkata"
        }
      };

      // Remove undefined fields
      if (!bookingPayload.booking_data.customer_id) {
        delete bookingPayload.booking_data.customer_id;
      }

      // Call booking API
      const response = await api.post('/planify/booking/public/booking', bookingPayload);
      
      if (response.data && response.data.success === true) {
        // Show success modal
        setModalType('success');
        setModalTitle('Booking Successful!');
        setModalMessage('Your appointment has been booked successfully. You will receive a confirmation email shortly.');
        setShowModal(true);
      } else {
        // Show error modal for API response with success: false
        setModalType('error');
        setModalTitle('Booking Failed');
        setModalMessage(response.data?.message || 'Booking failed. Please try again.');
        setShowModal(true);
      }
    } catch (error) {
      console.error('Booking error:', error);
      // Show error modal
      setModalType('error');
      setModalTitle('Booking Failed');
      setModalMessage(error.response?.data?.message || error.message || 'Please try again');
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Progress indicator component
  const ProgressIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-2 md:space-x-4">
        {[1, 2, 3, 4, 5].map((step) => (
          <React.Fragment key={step}>
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                step <= currentStep
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {step < currentStep ? '✓' : step}
            </div>
            {step < 5 && (
              <div
                className={`h-1 w-8 md:w-16 transition-colors ${
                  step < currentStep ? 'bg-blue-500' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // Step titles
  const stepTitles = [
    'Select Service',
    'Choose Date & Time',
    'Patient Information',
    'Medical History',
    'Confirm Booking'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-6 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Book Your Appointment</h1>
          <p className="text-blue-100">Complete the form below to schedule your mental health consultation</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Progress Indicator */}
        <ProgressIndicator />

        {/* Current Step Title */}
        <div className="text-center mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
            Step {currentStep}: {stepTitles[currentStep - 1]}
          </h2>
        </div>

        {/* Form Steps */}
        <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
          <AnimatePresence mode="wait">
            {/* Step 1: Select Service */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Provider Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Provider <span className="text-red-500">*</span>
                  </label>
                  {loadingProviders ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <span className="ml-3 text-gray-600">Loading providers...</span>
                    </div>
                  ) : error ? (
                    <div className="text-center py-8">
                      <p className="text-red-500 mb-3">{error.message}</p>
                      <button
                        onClick={loadProviders}
                        className="text-blue-500 hover:text-blue-600 font-medium"
                      >
                        Try Again →
                      </button>
                    </div>
                  ) : providers.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {providers.map((provider) => (
                        <div
                          key={provider.user_id}
                          onClick={() => handleProviderSelection(provider.user_id)}
                          className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                            selectedProvider === provider.user_id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-3xl">👩‍⚕️</span>
                            <div className="flex-1">
                              <p className="font-medium">{provider.user_name}</p>
                              <p className="text-sm text-gray-600">{provider.professional_title}</p>
                              <p className="text-sm text-gray-500">₹{provider.consultation_fee}/session</p>
                              {provider.organization_name && (
                                <p className="text-xs text-gray-400">{provider.organization_name}</p>
                              )}
                            </div>
                            {selectedProvider === provider.user_id && (
                              <span className="text-blue-500">✓</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500 mb-3">No providers available</p>
                      <Link
                        to="/booking/find-provider"
                        className="text-blue-500 hover:text-blue-600 font-medium"
                      >
                        Browse Providers →
                      </Link>
                    </div>
                  )}
                  {errors.provider && (
                    <p className="text-red-500 text-sm mt-1">{errors.provider}</p>
                  )}
                </div>

                {/* Appointment Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Appointment Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {appointmentTypes.map((type) => (
                      <div
                        key={type.value}
                        onClick={() => setAppointmentType(type.value)}
                        className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                          appointmentType === type.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-2xl">{type.icon}</span>
                              <p className="font-medium">{type.label}</p>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{type.duration}</p>
                          </div>
                          {appointmentType === type.value && (
                            <span className="text-blue-500">✓</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {errors.type && (
                    <p className="text-red-500 text-sm mt-1">{errors.type}</p>
                  )}
                </div>

                {/* Consultation Mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Consultation Mode <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      // { value: 'video', label: 'Video Call', icon: '📹', desc: 'Online consultation via secure video' },
                      { value: 'in-person', label: 'In-Person', icon: '🏥', desc: 'Visit clinic for face-to-face session' },
                      // { value: 'phone', label: 'Phone Call', icon: '📞', desc: 'Voice call consultation' }
                    ].map((mode) => (
                      <div
                        key={mode.value}
                        onClick={() => setConsultationMode(mode.value)}
                        className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                          consultationMode === mode.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center">
                          <span className="text-3xl">{mode.icon}</span>
                          <p className="font-medium mt-2">{mode.label}</p>
                          <p className="text-xs text-gray-500 mt-1">{mode.desc}</p>
                          {consultationMode === mode.value && (
                            <span className="text-blue-500 block mt-2">✓</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {errors.mode && (
                    <p className="text-red-500 text-sm mt-1">{errors.mode}</p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 2: Date & Time Selection */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Date Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Date <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-7 gap-2 mb-4">
                    {getNext30Days().map((date) => (
                      <button
                        key={date.value}
                        onClick={() => handleDateSelection(date.value)}
                        className={`p-3 text-center rounded-lg border transition-all ${
                          selectedDate === date.value
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'bg-white hover:bg-gray-50 border-gray-300'
                        }`}
                      >
                        <p className="text-xs">{date.label.split(',')[0]}</p>
                        <p className="font-semibold text-sm">{date.label.split(' ')[1]}</p>
                        {selectedDate === date.value && (
                          <p className="text-xs mt-1 opacity-75">Selected</p>
                        )}
                      </button>
                    ))}
                  </div>
                  {selectedDate && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">Selected Date:</span> {
                          new Date(selectedDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        }
                      </p>
                    </div>
                  )}
                  {errors.date && (
                    <p className="text-red-500 text-sm mt-1">{errors.date}</p>
                  )}
                </div>

                {/* Time Slot Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Time <span className="text-red-500">*</span>
                  </label>
                  {selectedDate ? (
                    <div>
                      <div className="mb-3">
                        <p className="text-sm text-gray-600">Available slots for {
                          new Date(selectedDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric'
                          })
                        }</p>
                      </div>
                      {loadingSlots ? (
                        <div className="flex justify-center items-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                          <span className="ml-3 text-gray-600">Loading available slots...</span>
                        </div>
                      ) : availableSlots.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {availableSlots.map((slot, index) => (
                            <button
                              key={slot.id || index}
                              onClick={() => setSelectedTime(slot.display_time)}
                              className={`px-4 py-3 text-sm rounded-lg border transition-all ${
                                selectedTime === slot.display_time
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'bg-white hover:bg-gray-50 border-gray-300'
                              }`}
                            >
                              <div className="text-center">
                                <p className="font-medium">{slot.start_display}</p>
                                <p className="text-xs opacity-75">to {slot.end_display}</p>
                              </div>
                            </button>
                          ))}
                      </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 mb-3">No available slots for this date</p>
                          <p className="text-sm text-gray-400">Please select a different date</p>
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-4 text-xs">
                        <span className="flex items-center">
                          <span className="w-3 h-3 bg-white border border-gray-300 rounded mr-1"></span>
                          Available
                        </span>
                        <span className="flex items-center">
                          <span className="w-3 h-3 bg-blue-500 rounded mr-1"></span>
                          Selected
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500">Please select a date first</p>
                  )}
                  {errors.time && (
                    <p className="text-red-500 text-sm mt-1">{errors.time}</p>
                  )}
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Session Duration
                  </label>
                  <select
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                    <option value="90">90 minutes</option>
                    <option value="120">120 minutes</option>
                  </select>
                </div>
              </motion.div>
            )}

            {/* Step 3: Patient Information */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* New/Existing Patient */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Are you a new patient?
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={isNewPatient}
                        onChange={() => setIsNewPatient(true)}
                        className="mr-2"
                      />
                      <span>Yes, I'm a new patient</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={!isNewPatient}
                        onChange={() => setIsNewPatient(false)}
                        className="mr-2"
                      />
                      <span>No, I've been here before</span>
                    </label>
                  </div>
                </div>

                {/* Customer ID for existing patients */}
                {!isNewPatient && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Patient ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={patientInfo.customerId}
                      onChange={(e) => setPatientInfo({...patientInfo, customerId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your patient ID"
                    />
                  </div>
                )}

                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={patientInfo.firstName}
                      onChange={(e) => setPatientInfo({...patientInfo, firstName: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter first name"
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={patientInfo.lastName}
                      onChange={(e) => setPatientInfo({...patientInfo, lastName: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter last name"
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={patientInfo.email}
                      onChange={(e) => setPatientInfo({...patientInfo, email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="email@example.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={patientInfo.phone}
                      onChange={(e) => setPatientInfo({...patientInfo, phone: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 98765 43210"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>
                </div>

                {/* Personal Information - Only for new patients */}
                {isNewPatient && (
                  <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={patientInfo.dateOfBirth}
                      onChange={(e) => setPatientInfo({...patientInfo, dateOfBirth: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.dateOfBirth && (
                      <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={patientInfo.gender}
                      onChange={(e) => setPatientInfo({...patientInfo, gender: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer-not-to-say">Prefer not to say</option>
                    </select>
                    {errors.gender && (
                      <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={patientInfo.address}
                    onChange={(e) => setPatientInfo({...patientInfo, address: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Street address"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={patientInfo.city}
                      onChange={(e) => setPatientInfo({...patientInfo, city: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <select
                      value={patientInfo.state}
                      onChange={(e) => setPatientInfo({...patientInfo, state: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Kerala">Kerala</option>
                      <option value="Tamil Nadu">Tamil Nadu</option>
                      <option value="Karnataka">Karnataka</option>
                      <option value="Maharashtra">Maharashtra</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PIN Code
                    </label>
                    <input
                      type="text"
                      value={patientInfo.pincode}
                      onChange={(e) => setPatientInfo({...patientInfo, pincode: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="6 digits"
                      maxLength="6"
                    />
                  </div>
                </div>
                  </>
                )}

                {/* Emergency Contact */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Emergency Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Name
                      </label>
                      <input
                        type="text"
                        value={patientInfo.emergencyContact}
                        onChange={(e) => setPatientInfo({...patientInfo, emergencyContact: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Emergency contact name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Phone
                      </label>
                      <input
                        type="tel"
                        value={patientInfo.emergencyPhone}
                        onChange={(e) => setPatientInfo({...patientInfo, emergencyPhone: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="+1 98765 43210"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Medical History */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Primary Concern */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    What is your primary concern? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={medicalInfo.primaryConcern}
                    onChange={(e) => setMedicalInfo({...medicalInfo, primaryConcern: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    placeholder="Please describe what brings you here today..."
                  />
                  {errors.primaryConcern && (
                    <p className="text-red-500 text-sm mt-1">{errors.primaryConcern}</p>
                  )}
                </div>

                {/* Symptoms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select all symptoms you\'re experiencing <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {commonSymptoms.map((symptom) => (
                      <label
                        key={symptom}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={medicalInfo.symptoms.includes(symptom)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setMedicalInfo({
                                ...medicalInfo,
                                symptoms: [...medicalInfo.symptoms, symptom]
                              });
                            } else {
                              setMedicalInfo({
                                ...medicalInfo,
                                symptoms: medicalInfo.symptoms.filter(s => s !== symptom)
                              });
                            }
                          }}
                          className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{symptom}</span>
                      </label>
                    ))}
                  </div>
                  {errors.symptoms && (
                    <p className="text-red-500 text-sm mt-1">{errors.symptoms}</p>
                  )}
                </div>

                {/* Previous Therapy */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Have you had therapy or counseling before?
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="yes"
                        checked={medicalInfo.previousTherapy === 'yes'}
                        onChange={(e) => setMedicalInfo({...medicalInfo, previousTherapy: e.target.value})}
                        className="mr-2"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="no"
                        checked={medicalInfo.previousTherapy === 'no'}
                        onChange={(e) => setMedicalInfo({...medicalInfo, previousTherapy: e.target.value})}
                        className="mr-2"
                      />
                      <span>No</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="prefer-not-to-say"
                        checked={medicalInfo.previousTherapy === 'prefer-not-to-say'}
                        onChange={(e) => setMedicalInfo({...medicalInfo, previousTherapy: e.target.value})}
                        className="mr-2"
                      />
                      <span>Prefer not to say</span>
                    </label>
                  </div>
                </div> */}

                {/* Current Medications */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Medications (if any)
                  </label>
                  <textarea
                    value={medicalInfo.medications}
                    onChange={(e) => setMedicalInfo({...medicalInfo, medications: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="List any medications you are currently taking..."
                  />
                </div> */}

                {/* Allergies */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allergies (if any)
                  </label>
                  <input
                    type="text"
                    value={medicalInfo.allergies}
                    onChange={(e) => setMedicalInfo({...medicalInfo, allergies: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="List any allergies..."
                  />
                </div> */}

                {/* Additional Notes */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Anything else you\'d like us to know?
                  </label>
                  <textarea
                    value={medicalInfo.additionalNotes}
                    onChange={(e) => setMedicalInfo({...medicalInfo, additionalNotes: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Any additional information that might be helpful..."
                  />
                </div> */}
              </motion.div>
            )}

            {/* Step 5: Payment & Confirmation */}
            {currentStep === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Appointment Summary */}
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Appointment Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Provider:</span>
                      <span className="font-medium">
                        {selectedProviderData?.user_name || 'Not selected'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium">
                        {appointmentTypes.find(t => t.value === appointmentType)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mode:</span>
                      <span className="font-medium capitalize">{consultationMode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium">
                        {selectedDate && new Date(selectedDate).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time:</span>
                      <span className="font-medium">{selectedTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">
                        {(() => {
                          const selectedSlot = availableSlots.find(slot => slot.display_time === selectedTime);
                          if (selectedSlot) {
                            const startTime = new Date(selectedSlot.start_time);
                            const endTime = new Date(selectedSlot.end_time);
                            const durationMs = endTime - startTime;
                            const durationMinutes = Math.round(durationMs / (1000 * 60));
                            return `${durationMinutes} minutes`;
                          }
                          return 'Select a time slot';
                        })()}
                      </span>
                    </div>
                    <div className="border-t pt-3 flex justify-between">
                      <span className="text-gray-900 font-semibold">Total Fee:</span>
                      <span className="text-xl font-bold text-blue-600">
                        ₹{selectedProviderData?.consultation_fee || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Patient Information Summary */}
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Patient Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium">
                        {patientInfo.firstName && patientInfo.lastName 
                          ? `${patientInfo.firstName} ${patientInfo.lastName}`
                          : 'Not provided'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium">
                        {patientInfo.email || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Phone:</span>
                      <span className="font-medium">
                        {patientInfo.phone || 'Not provided'}
                      </span>
                    </div>
                    {!isNewPatient && patientInfo.customerId && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Patient ID:</span>
                        <span className="font-medium">
                          {patientInfo.customerId}
                        </span>
                      </div>
                    )}
                    {isNewPatient && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date of Birth:</span>
                          <span className="font-medium">
                            {patientInfo.dateOfBirth || 'Not provided'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Gender:</span>
                          <span className="font-medium capitalize">
                            {patientInfo.gender || 'Not provided'}
                          </span>
                        </div>
                      </>
                    )}
                    {patientInfo.emergencyContact && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Emergency Contact:</span>
                        <span className="font-medium">
                          {patientInfo.emergencyContact}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Insurance Information - Hidden for now */}
                {/* <div>
                  <label className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      checked={insuranceInfo.hasInsurance}
                      onChange={(e) => setInsuranceInfo({...insuranceInfo, hasInsurance: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="font-medium">I have health insurance</span>
                  </label>
                  
                  {insuranceInfo.hasInsurance && (
                    <div className="space-y-4 bg-gray-50 rounded-lg p-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Insurance Provider <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={insuranceInfo.provider}
                          onChange={(e) => setInsuranceInfo({...insuranceInfo, provider: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Insurance company name"
                        />
                        {errors.insuranceProvider && (
                          <p className="text-red-500 text-sm mt-1">{errors.insuranceProvider}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Policy Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={insuranceInfo.policyNumber}
                            onChange={(e) => setInsuranceInfo({...insuranceInfo, policyNumber: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Policy number"
                          />
                          {errors.policyNumber && (
                            <p className="text-red-500 text-sm mt-1">{errors.policyNumber}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Group Number
                          </label>
                          <input
                            type="text"
                            value={insuranceInfo.groupNumber}
                            onChange={(e) => setInsuranceInfo({...insuranceInfo, groupNumber: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Group number (optional)"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div> */}

                {/* Payment Method - Hidden for now */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { value: 'card', label: 'Credit/Debit Card', icon: '💳' },
                      { value: 'upi', label: 'UPI Payment', icon: '📱' },
                      { value: 'cash', label: 'Cash at Clinic', icon: '💵' }
                    ].map((method) => (
                      <div
                        key={method.value}
                        onClick={() => setPaymentMethod(method.value)}
                        className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                          paymentMethod === method.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center">
                          <span className="text-2xl">{method.icon}</span>
                          <p className="font-medium mt-2">{method.label}</p>
                          {paymentMethod === method.value && (
                            <span className="text-blue-500 block mt-1">✓</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {errors.payment && (
                    <p className="text-red-500 text-sm mt-1">{errors.payment}</p>
                  )}
                </div> */}

                {/* Terms & Conditions */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 mr-2"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      I agree to the{' '}
                      <Link to="/terms" className="text-blue-500 hover:text-blue-600">
                        Terms & Conditions
                      </Link>
                      {' '}and{' '}
                      <Link to="/privacy" className="text-blue-500 hover:text-blue-600">
                        Privacy Policy
                      </Link>
                      <span className="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                  {errors.terms && (
                    <p className="text-red-500 text-sm mt-1">{errors.terms}</p>
                  )}
                </div>

                {/* Important Notes */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-2">Important Information</h4>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• Please arrive 10 minutes early for your first appointment</li>
                    {/* <li>• For video consultations, ensure a stable internet connection</li>
                    <li>• Have your insurance card ready if using insurance</li> */}
                    <li>• You will receive confirmation via email and SMS</li>
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            {currentStep > 1 && (
              <button
                onClick={handlePreviousStep}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                ← Previous
              </button>
            )}
            
            {currentStep < 5 ? (
              <button
                onClick={handleNextStep}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium ml-auto"
              >
                Next Step →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-8 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Booking...
                  </span>
                ) : (
                  'Confirm Booking'
                )}
              </button>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Need Help?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link
              to="/booking/find-provider"
              className="text-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <span className="text-2xl">🔍</span>
              <p className="mt-2 font-medium">Find Provider</p>
              <p className="text-xs text-gray-600">Browse all providers</p>
            </Link>
            <Link
              to="/booking/quick-booking"
              className="text-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <span className="text-2xl">🚀</span>
              <p className="mt-2 font-medium">Quick Booking</p>
              <p className="text-xs text-gray-600">Book in 2 minutes</p>
            </Link>
            <Link
              to="/contact"
              className="text-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <span className="text-2xl">📞</span>
              <p className="mt-2 font-medium">Contact Support</p>
              <p className="text-xs text-gray-600">+1 (888) 404-7470</p>
            </Link>
          </div>
        </div>
      </div>

      {/* Success/Error Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-center mb-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  modalType === 'success' 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-red-100 text-red-600'
                }`}>
                  {modalType === 'success' ? (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Modal Content */}
              <div className="text-center">
                <h3 className={`text-xl font-semibold mb-2 ${
                  modalType === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {modalTitle}
                </h3>
                <p className="text-gray-600 mb-6">
                  {modalMessage}
                </p>

                {/* Modal Actions */}
                <div className="flex gap-3">
                  {modalType === 'success' ? (
                    <>
                      <button
                        onClick={() => {
                          setShowModal(false);
                          // Reset form to step 1
                          setCurrentStep(1);
                          setSelectedProvider('');
                          setSelectedProviderData(null);
                          setSelectedDate('');
                          setSelectedTime('');
                          setPatientInfo({
                            firstName: '',
                            lastName: '',
                            email: '',
                            phone: '',
                            dateOfBirth: '',
                            gender: '',
                            address: '',
                            city: '',
                            state: 'Kerala',
                            pincode: '',
                            emergencyContact: '',
                            emergencyPhone: '',
                            customerId: ''
                          });
                          setMedicalInfo({
                            primaryConcern: '',
                            symptoms: [],
                            previousTherapy: '',
                            medications: '',
                            allergies: '',
                            additionalNotes: ''
                          });
                        }}
                        className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Book Another Appointment
                      </button>
                      <button
                        onClick={() => {
                          setShowModal(false);
                          navigate('/booking/find-provider');
                        }}
                        className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Close
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setShowModal(false)}
                      className="w-full bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Try Again
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookAppointment;